'use client';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { trackSpecialistProfileCreated, trackSpecIntent } from '@/lib/analytics';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { useAuthStore } from '@/store/auth.store';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { TIMEZONES, getBrowserTimezone } from '@/lib/timezone';
import CityAutocomplete from '@/components/ui/CityAutocomplete';

export default function BecomeSpecialistPage() {
  const { user, isAuthenticated } = useAuthStore();
  const router = useRouter();
  const { setUser } = useAuthStore();
  const queryClient = useQueryClient();
  const { register, handleSubmit, watch, setValue } = useForm();
  const [agreePublic, setAgreePublic] = useState(false);
  const [agreeError, setAgreeError] = useState('');

  useEffect(() => {
    if (!isAuthenticated()) router.push('/login');
  }, []);

  // Цель spec_intent: задержался на странице 30 сек — считаем интерес к роли
  // специалиста. Один раз за сессию, флаг в sessionStorage.
  useEffect(() => {
    const KEY = 'sp_spec_intent_sent';
    try {
      if (sessionStorage.getItem(KEY)) return;
    } catch { return; }
    const t = setTimeout(() => {
      trackSpecIntent();
      try { sessionStorage.setItem(KEY, '1'); } catch {}
    }, 30_000);
    return () => clearTimeout(t);
  }, []);

  const handleCreate = (data: any) => {
    if (!agreePublic) {
      setAgreeError('Необходимо дать согласие на публикацию профиля');
      return;
    }
    setAgreeError('');
    createProfile.mutate(data);
  };

  const createProfile = useMutation({
    mutationFn: (data: any) => api.post('/specialists/me/profile', {
      bio: data.bio || undefined,
      experience_years: data.experience_years ? Number(data.experience_years) : undefined,
      city: data.city || undefined,
      timezone: data.timezone,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['specialist-profile'] });
      notify.success('Профиль специалиста создан!');
      trackSpecialistProfileCreated();
      router.push('/dashboard');
    },
    onError: (err: any) => notify.apiError(err, 'Ошибка создания профиля'),
  });

  if (!isAuthenticated()) return null;

  return (
    <div className="min-h-screen bg-bg-subtle py-12">
      <div className="max-w-xl mx-auto px-4">
        <div className="bg-bg-surface rounded-2xl border border-border p-8">
          <h1 className="text-2xl font-bold text-text-primary mb-2">Стать исполнителем</h1>
          <p className="text-gray-500 text-sm mb-8">Заполните профиль чтобы начать получать заказы</p>

          <form onSubmit={handleSubmit(handleCreate)} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-ui mb-1.5">Город</label>
                <CityAutocomplete
                  strict
                  value={watch('city') || ''}
                  onChange={val => setValue('city', val, { shouldDirty: true })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-ui mb-1.5">Опыт (лет)</label>
                <input {...register('experience_years')} type="number" min="0" max="50"
                  placeholder="0"
                  className="w-full border border-border-input rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-ui mb-1.5">О себе</label>
              <textarea {...register('bio')} rows={4}
                placeholder="Расскажите о вашем опыте и навыках..."
                className="w-full border border-border-input rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-ui mb-1.5">Часовой пояс</label>
              <select {...register('timezone')} defaultValue={getBrowserTimezone()}
                className="w-full border border-border-input rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand bg-bg-surface">
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
            {/* Согласие на распространение ПДн (ст. 10.1 ФЗ-152) */}
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreePublic}
                  onChange={e => { setAgreePublic(e.target.checked); if (e.target.checked) setAgreeError(''); }}
                  className="mt-0.5 w-4 h-4 rounded border-border-input accent-brand flex-shrink-0"
                />
                <span className="text-sm text-text-secondary leading-relaxed">
                  Я даю согласие на публичное размещение моего профиля (имя, город, фото, описание, рейтинг) в открытом доступе на сервисе СвойПрофи в соответствии со{' '}
                  <a href="/privacy" target="_blank" className="text-brand hover:underline font-medium">
                    ст. 10.1 ФЗ-152
                  </a>
                </span>
              </label>
              {agreeError && <p className="text-red-600 text-xs mt-2 font-medium">{agreeError}</p>}
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => router.back()}
                className="flex-1 border border-border-input text-text-ui px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-bg-subtle transition-colors">
                Назад
              </button>
              <button type="submit" disabled={createProfile.isPending}
                className="flex-1 bg-brand text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50">
                {createProfile.isPending ? 'Создание...' : 'Создать профиль'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
